<template>
	<div class="m-footer" :class="{ 'm-footer--en': isEn }" :lang="isEn ? 'en' : 'zh-TW'">
		<div class="m-footer-content">
			<div class="info">
				<img class="logo" :src="footerLogoSrc" alt="logo" />
				<div class="info-item">
					<div class="label">{{ footerCopy.serviceHoursLabel }}</div>
					<div class="value">{{ footerCopy.serviceHoursValue }}</div>
				</div>
				<div class="info-item">
					<div class="label">{{ footerCopy.companyNameLabel }}</div>
					<div class="value">FAITH ONNET LIMITED</div>
				</div>
				<div class="info-item">
					<div class="label">{{ footerCopy.addressLabel }}</div>
					<div class="value">
						Room 2003, 20th Floor, COFCO TOWER, No. 262 Gloucester Road, Causeway Bay, Hong Kong
					</div>
				</div>
				<div class="info-item">
					<div class="label">{{ footerCopy.contactEmailLabel }}</div>
					<div class="value">service@seeronnet.com</div>
				</div>

				<div class="info-item">
					<div class="label">{{ footerCopy.businessEmailLabel }}</div>
					<div class="value">tarry@seeronnet.com</div>
				</div>
			</div>

			<div class="follows">
				<div class="title">{{ footerCopy.followTitle }}</div>
				<ul class="follow-list">
					<li v-for="(item, index) in contactList" :key="index" @click="toMedia(item)">
						<img :src="getContactIcon(item.icon)" />
					</li>
				</ul>
				<!-- <ClientOnly>
					<div class="subscribe" v-if="!isLogin">
						<n-auto-complete
							v-model:value="email"
							:input-props="{
								autocomplete: 'disabled',
							}"
							:options="options"
							placeholder="請輸入郵箱"
							clearable
						/>
						<div :class="email.length > 0 ? 'btn' : 'btn no-submit'" @click="toLogin">訂閱</div>
					</div>
				</ClientOnly> -->
			</div>

			<div class="we-accept">
				<div class="title">{{ footerCopy.weAcceptTitle }}</div>
				<ul>
					<img class="pay-icon" v-for="(v, i) in payIconList" :key="i" :src="v" alt="" />
				</ul>
			</div>

			<div class="link-box">
				<div class="company-information">
					<div class="title">{{ footerCopy.companyTitle }}</div>
					<ul>
						<li
							@click="handleFooterLink(item)"
							v-for="(item, index) in companyInformation"
							:key="index"
						>
							{{ item.text }}
						</li>
					</ul>
				</div>
				<div class="helps">
					<div class="title">{{ footerCopy.helpTitle }}</div>
					<ul>
						<li @click="handleFooterLink(item)" v-for="(item, index) in helps" :key="index">
							{{ item.text }}
						</li>
					</ul>
				</div>
			</div>

			<div class="bottom-wrap">
				<ul class="xieyi-list">
					<li v-for="(item, index) in xieyiList" :key="index" @click="handleFooterLink(item)">
						<span>{{ item.name }}</span>
					</li>
				</ul>
				<p class="copyright">{{ footerCopy.copyright }}</p>
			</div>
		</div>
	</div>
</template>

<script setup>
import { goLink } from "~/utils/common";
import { computed, inject, ref } from "vue";
import { emailSuffixes, KEFU_LINK } from "~/utils/constants";
import { sendFollowEmail } from "~/api/api";
import EmailValidator from "email-validator";
import showMessage from "~/utils/showMessage";
import {
	getEmailDomain,
	resolveSocialPlatform,
	useSiteComponentTracking,
} from "~/composables/useSiteComponentTracking";
// 导入所有支付图标
import visaIcon from "./images/pay-icon-list/visa.png";
import paypalIcon from "./images/pay-icon-list/paypal.png";
import octopusIcon from "./images/pay-icon-list/octopus.png";
import paymeIcon from "./images/pay-icon-list/payme.png";
import alipayIcon from "./images/pay-icon-list/alipay.png";
import wechatpayIcon from "./images/pay-icon-list/WeChatpay.png";
import linepayIcon from "./images/pay-icon-list/linepay.png";
import pxpayplusIcon from "./images/pay-icon-list/pxpayplus.png";
import icashIcon from "./images/pay-icon-list/icash.png";
import myacrdIcon from "./images/pay-icon-list/myacrd.png";
import touchIcon from "./images/pay-icon-list/touch.png";

// 导入其他图片
import logoImg from "./images/logo.png";
import mLogoImg from "./images/m_logo.png";
import mFooterBgImg from "./images/m_footer_bg.png";
import pcFooterBgImg from "./images/pc_footer_bg.png";
import facebookIcon from "./images/facebook@2x (1).png";
import insIcon from "./images/ins@2x.png";
import lineIcon from "./images/Line@2x (1).png";
import whatsappIcon from "./images/WhatsApp@2x.png";
import youtubeIcon from "./images/YouTube@2x.png";
import tiktokIcon from "./images/tiktok@2x.png";
import xIcon from "./images/x@2x.png";

// 导入剩余的支付图标
import fpxIcon from "./images/pay-icon-list/FPX.png";
import shopeeIcon from "./images/pay-icon-list/shopee.png";
import grabIcon from "./images/pay-icon-list/grab.png";
import danaIcon from "./images/pay-icon-list/dana.png";
import footerLogoZh from "~/assets/images/SeerQtw.svg";
import footerLogoEn from "~/assets/images/SeerQ.svg";

const props = defineProps({
	/** 傳入 `en` 時展示英文文案，其餘（如 zh-TW）為繁中 */
	lang: {
		type: String,
		default: "zh-TW",
	},
	isLogin: {
		type: Boolean,
		default: false,
	},
});

const isEn = computed(() => props.lang === "en");
const { track } = useSiteComponentTracking("mobile");

const footerLogoSrc = computed(() => (isEn.value ? footerLogoEn : footerLogoZh));

const footerCopy = computed(() =>
	isEn.value
		? {
				companyTitle: "Company",
				helpTitle: "Help",
				serviceHoursLabel: "Service Hours:",
				serviceHoursValue: "Mon – Fri 09:00 – 18:00",
				companyNameLabel: "Company Name:",
				addressLabel: "Address:",
				contactEmailLabel: "Contact Email:",
				businessEmailLabel: "Business Cooperation:",
				weAcceptTitle: "We Accept",
				followTitle: "Follow SeerQ",
				copyright: "Copyright © 2026 SeerQ. All Rights Reserved.",
			}
		: {
				companyTitle: "公司資訊",
				helpTitle: "幫助",
				serviceHoursLabel: "服務時間：",
				serviceHoursValue: "週一至週五 09:00 - 18:00",
				companyNameLabel: "公司名稱：",
				addressLabel: "聯絡地址：",
				contactEmailLabel: "聯絡電郵：",
				businessEmailLabel: "商務合作電郵：",
				weAcceptTitle: "我們接受",
				followTitle: "追蹤先知命局",
				copyright: "Copyright © 2026先知命局 版權所有",
			}
);

const email = ref("");
const openEmailOfficail = inject("openEmailOfficail");

const toLogin = () => {
	let urlObj = typeof window !== "undefined" ? new URL(window.location.href) : null;
	if (!urlObj) return;

	if (email.value?.length == 0) {
		track("footer_subscribe_submit", {
			result: "fail",
		});
		showMessage(isEn.value ? "Email is required" : "邮箱不能为空");
		return;
	}

	const emailDomain = getEmailDomain(email.value);
	if (EmailValidator.validate(email.value)) {
		sendFollowEmail({
			email: email.value,
			return_url: `${urlObj.protocol}//${urlObj.host}/`,
		})
			.then(() => {
				track("footer_subscribe_submit", {
					email_domain: emailDomain,
					result: "success",
				});
				openEmailOfficail?.();
			})
			.catch(() => {
				track("footer_subscribe_submit", {
					email_domain: emailDomain,
					result: "fail",
				});
			});
	} else {
		track("footer_subscribe_submit", {
			email_domain: emailDomain,
			result: "fail",
		});
		showMessage(isEn.value ? "Invalid email format" : "郵箱格式錯誤");
	}
};

// 邮箱后缀
const options = computed(() => {
	return emailSuffixes.map(suffix => {
		const inputValue = email.value?.split("@")[0];
		return {
			label: inputValue + suffix,
			value: inputValue + suffix,
		};
	});
});
// 根据域名修改主体跟Slogan
// const hspDomain = "www.hspiritlink.com";
// const isHspDomain = ref(false); //是否是 www.hspiritlink.com 域名
// onMounted(() => {
// 	if (import.meta.client) {
// 		if (location.hostname === hspDomain) {
// 			isHspDomain.value = true;
// 		}
// 	}
// });

// 联系方式
const contactList = [
	{
		icon: "YouTube.png",
		url: "https://www.youtube.com/@seerq-001?si=XI7QAzjGeR0fRT4R",
	},
	{
		icon: "WhatsApp.png",
		url: "https://api.whatsapp.com/send?phone=886978572929",
	},
	{
		icon: "x.png",
		url: "https://twitter.com/Seer144859",
	},
	{
		icon: "facebook.png",
		url: "https://www.facebook.com/profile.php?id=61578513161082",
	},
	{
		icon: "Line.png",
		url: "https://lin.ee/P06Koie",
	},
	{
		icon: "ins.png",
		url: "https://www.instagram.com/official.seeronnet/",
	},
	{
		icon: "tiktok.png",
		url: "https://www.tiktok.com/@official.seer",
	},
];

function handleFooterLink(item) {
	track("footer_link_click", {
		element_id: item.elementId,
		element_text: item.text || item.name,
		target_url: item.url,
	});
	if (item.url?.startsWith("/")) {
		navigateTo(item.url);
		return;
	}
	goLink(item.url);
}

const toMedia = item => {
	track("footer_social_click", {
		social_platform: resolveSocialPlatform(item.icon),
		target_url: item.url,
	});
	goLink(item.url);
};

const protocolLang = computed(() => (isEn.value ? "en_us" : "zh"));

const xieyiList = computed(() => {
	const q = `lang=${protocolLang.value}`;
	if (isEn.value) {
		return [
			{
				text: "Privacy Policy Statement",
				url: `/dist/protocol/website/privacy-policy-statement?${q}`,
				name: "Privacy Policy Statement",
				elementId: "footer_privacy_policy",
			},
			{
				text: "Personal Information Collection Statement",
				url: `/dist/protocol/website/personal-info-collection-statement?${q}`,
				name: "Personal Information Collection Statement",
				elementId: "footer_personal_info",
			},
			{
				text: "Copyright Notice and Takedown Procedure",
				url: `/dist/protocol/website/copyright-notice-takedown?${q}`,
				name: "Copyright Notice and Takedown Procedure",
				elementId: "footer_copyright_notice",
			},
			{
				text: "Platform Terms of Use",
				url: `/dist/protocol/website/platform-terms-of-use?${q}`,
				name: "Platform Terms of Use",
				elementId: "footer_platform_terms",
			},
			{
				text: "Service Agreement",
				url: `/dist/protocol/website/service-agreement?${q}`,
				name: "Service Agreement",
				elementId: "footer_service_agreement",
			},
			{
				text: "Refund Policy",
				url: `/dist/protocol/website/refund-policy?${q}`,
				name: "Refund Policy",
				elementId: "footer_refund_policy",
			},
		];
	}
	return [
		{
			text: "私隱政策聲明",
			url: `/dist/protocol/website/privacy-policy-statement?${q}`,
			name: "私隱政策聲明",
			elementId: "footer_privacy_policy",
		},
		{
			text: "個人資料收集聲明",
			url: `/dist/protocol/website/personal-info-collection-statement?${q}`,
			name: "個人資料收集聲明",
			elementId: "footer_personal_info",
		},
		{
			text: "著作權通知和移除程序",
			url: `/dist/protocol/website/copyright-notice-takedown?${q}`,
			name: "著作權通知和移除程序",
			elementId: "footer_copyright_notice",
		},
		{
			text: "平台使用條款",
			url: `/dist/protocol/website/platform-terms-of-use?${q}`,
			name: "平台使用條款",
			elementId: "footer_platform_terms",
		},
		{
			text: "服務協議",
			url: `/dist/protocol/website/service-agreement?${q}`,
			name: "服務協議",
			elementId: "footer_service_agreement",
		},
		{
			text: "退款政策",
			url: `/dist/protocol/website/refund-policy?${q}`,
			name: "退款政策",
			elementId: "footer_refund_policy",
		},
	];
});

const companyInformation = computed(() =>
	isEn.value
		? [
				{
					text: "About SeerQ",
					url: "/seer/aboutus",
					elementId: "footer_about",
				},
				{
					text: "Newsroom",
					url: "/seer/aboutus?to=newsroom",
					elementId: "footer_newsroom",
				},
			]
		: [
				{
					text: "關於先知命局",
					url: "/seer/aboutus",
					elementId: "footer_about",
				},
				{
					text: "新聞室",
					url: "/seer/aboutus?to=newsroom",
					elementId: "footer_newsroom",
				},
			]
);

const helps = computed(() =>
	isEn.value
		? [
				{
					text: "Online Customer Support",
					url: KEFU_LINK,
					elementId: "footer_help_center",
				},
				{
					text: "Partner with SeerQ",
					url: "/seer/aboutus?to=cooperate",
					elementId: "footer_cooperate",
				},
			]
		: [
				{
					text: "線上客戶支援",
					url: KEFU_LINK,
					elementId: "footer_help_center",
				},
				{
					text: "與先知命局合作",
					url: "/seer/aboutus?to=cooperate",
					elementId: "footer_cooperate",
				},
			]
);

const payIconMap = {
	visa: visaIcon,
	paypal: paypalIcon,
	octopus: octopusIcon,
	payme: paymeIcon,
	alipay: alipayIcon,
	WeChatpay: wechatpayIcon,
	linepay: linepayIcon,
	pxpayplus: pxpayplusIcon,
	icash: icashIcon,
	myacrd: myacrdIcon,
	touch: touchIcon,
	FPX: fpxIcon,
	shopee: shopeeIcon,
	grab: grabIcon,
	dana: danaIcon,
};

const contactIconMap = {
	"YouTube.png": youtubeIcon,
	"WhatsApp.png": whatsappIcon,
	"x.png": xIcon,
	"facebook.png": facebookIcon,
	"Line.png": lineIcon,
	"ins.png": insIcon,
	"tiktok.png": tiktokIcon,
};

const getContactIcon = iconName => {
	return contactIconMap[iconName] || "";
};

const payIconList = [
	"visa",
	"paypal",
	"octopus",
	"payme",
	"alipay",
	"WeChatpay",
	"linepay",
	"pxpayplus",
	"icash",
	"myacrd",
	"touch",
	"FPX",
	"shopee",
	"grab",
	"dana",
].map(v => {
	return payIconMap[v];
});
</script>

<script>
export default {
	name: "MFooter",
};
</script>

<style lang="scss" scoped>
.m-footer {
	width: 750px;
	background: #fff;
	overflow: hidden;
	border-top: 1px solid #efefef;

	.m-footer-content {
		padding: 64px 25px;
		margin: 0 auto;
		width: 750px;
		display: flex;
		flex-direction: column;
		overflow: hidden;

		.info {
			width: 420px;
			font-size: 15px;
			color: #333333;
			line-height: 24px;

			.logo {
				margin-bottom: 38px;
				display: block;
				width: 242px;
			}

			.info-item {
				display: flex;
				width: 686px;
				font-size: 28px;
				color: #333333;
				line-height: 42px;

				.label {
					white-space: nowrap;
				}
			}
		}

		.we-accept,
		.follows {
			margin-top: 64px;

			.title {
				margin-bottom: 36px;
				font-weight: bold;
				font-size: 32px;
				color: #333;
			}

			.pay-icon {
				margin-right: 15px;
				width: 160px;
			}

			.follow-list {
				display: flex;
				height: 64px;

				img {
					margin-right: 35px;
					width: 64px;
					height: 64px;
					cursor: pointer;
				}
			}

			.subscribe {
				margin-top: 36px;
				display: flex;
				align-items: stretch;
				width: 100%;

				:deep(.n-auto-complete) {
					flex: 1;
					width: auto;
				}

				.btn {
					width: 144px;
					height: 64px;
					background: #f2af19;
					border-radius: 0 8px 8px 0;
					text-align: center;
					line-height: 64px;
					font-size: 36px;
					color: #fff;
					letter-spacing: 4px;
					cursor: pointer;
				}

				.no-submit {
					opacity: 0.5;
					cursor: not-allowed;
				}

				:deep(.n-input) {
					width: 100% !important;
					height: 64px !important;
					line-height: 64px !important;
					border-radius: 8px 0 0 8px;
					border: 1px solid #efefef !important;
					border-right: none !important;
					--n-border: 1px solid #efefef !important;
					--n-border-focus: 1px solid #f2af19 !important;
					--n-border-hover: 1px solid #f2af19 !important;
					--n-box-shadow-focus: 0 0 0 0 rgba(242, 175, 25, 0.2) !important;
					--n-color: #fff !important;
					--n-color-focus: #fff !important;
					--n-color-disabled: #fff !important;
					--n-placeholder-color: #c9843d !important;
					--n-font-size: 32px !important;
				}
			}
		}

		.we-accept {
			flex: 1;
		}

		.link-box {
			margin-top: 50px;
			display: flex;
			justify-content: space-between;

			.company-information,
			.helps {
				.title {
					font-weight: bold;
					font-size: 32px;
					color: #333;
				}

				ul {
					margin-top: 36px;
					li {
						white-space: nowrap;
						font-size: 28px;
						color: #a0a0a0;
						line-height: 42px;
					}
				}
			}

			// .company-information {
			// 	li {
			// 		&:nth-child(2) {
			// 			// font-size: 24px;
			// 		}
			// 	}
			// }

			.helps {
				margin-right: 50px;
			}
		}

		.bottom-wrap {
			margin-top: 64px;
			padding-top: 64px;
			width: 702px;
			border-top: 1px solid #efefef;

			.xieyi-list {
				display: flex;
				align-items: center;
				justify-content: center;
				/* 允许自动换行 */
				flex-wrap: wrap;
				gap: 0 40px; /* 替代 margin，更适合换行布局 */
				font-size: 24px;
				color: #a0a0a0;
				list-style: none;
				padding: 0 10px;

				li {
					position: relative;
					/* 去掉禁止换行，允许自动换行 */
					/* white-space: nowrap; */
					line-height: 1.5; /* 保证多行文字垂直居中 */

					span {
						cursor: pointer;
					}

					/* 分隔竖线 */
					&:not(:nth-child(3)):not(:last-child)::after {
						content: "";
						width: 0;
						height: 28px;
						border-left: 1px solid #a0a0a0;
						position: absolute;
						top: 50%;
						right: -20px;
						transform: translateY(-50%);
					}
				}
			}

			.copyright {
				margin-top: 12px;
				font-size: 24px;
				color: #a0a0a0;
				text-align: center;
			}
		}
	}
}
</style>
